import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { showError, showSuccess } from '../utils/sweetAlert';
import logger from '../utils/logger';

const AddToProjectModal = ({ isOpen, onClose, recommendation, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    estimated_cost_aud: 0,
    estimated_savings_aud: 0,
    estimated_savings_kwh: 0,
    payback_years: 0,
    roi_pct: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false); // Prevent duplicate submissions

  useEffect(() => {
    if (recommendation && isOpen) {
      setFormData({
        title: recommendation.title || '',
        description: recommendation.description || '',
        priority: recommendation.priority || 'medium',
        estimated_cost_aud: recommendation.implementation_cost_aud || 0,
        estimated_savings_aud: recommendation.annual_savings_aud || 0,
        estimated_savings_kwh: recommendation.annual_savings_kwh || 0,
        payback_years: recommendation.payback_years || 0,
        roi_pct: recommendation.roi_percent || 0
      });
      // Reset submission state when modal opens
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  }, [recommendation, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmittingRef.current) {
      logger.warn('Submission already in progress, ignoring duplicate request');
      return;
    }
    
    isSubmittingRef.current = true;
    setSubmitting(true);

    try {
      const payload = {
        assessment_id: recommendation.assessment_id,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        estimated_cost_aud: parseFloat(formData.estimated_cost_aud),
        estimated_savings_aud: parseFloat(formData.estimated_savings_aud),
        estimated_savings_kwh: parseFloat(formData.estimated_savings_kwh),
        payback_years: parseFloat(formData.payback_years),
        roi_pct: parseFloat(formData.roi_pct),
        meta: {
          arc_code: recommendation.arc_code,
          original_recommendation_id: recommendation.id,
          category: recommendation.category,
          confidence_score: recommendation.confidence_score,
          recommendation_source: 'IAC_DATABASE'
        }
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/from-opportunity`,
        payload,
        { withCredentials: true }
      );

      // Reset submission state before closing
      isSubmittingRef.current = false;
      setSubmitting(false);
      
      // Call success callback to refresh projects list
      if (onSuccess) {
        onSuccess(response.data);
      }

      // Close modal
      onClose();
      
      // Show success message and ask if user wants to view project
      const result = await showSuccess(
        'Project Created!',
        'Would you like to view your new project?',
        true, // showCancelButton
        'View Project',
        'Stay Here'
      );
      
      if (result && result.isConfirmed && response.data?.project_id) {
        window.location.href = `/projects/${response.data.project_id}`;
      }

    } catch (error) {
      logger.error('Error creating project:', error);
      logger.log('Full error response:', JSON.stringify(error.response, null, 2));
      
      // Handle duplicate project error (409 Conflict)
      if (error.response?.status === 409) {
        isSubmittingRef.current = false;
        setSubmitting(false);
        const responseData = error.response?.data;
        logger.log('409 Response data:', responseData);
        logger.log('Detail type:', typeof responseData?.detail);
        logger.log('Detail value:', responseData?.detail);
        
        // Handle both object and string detail formats
        let existingProjectId, existingProjectTitle;
        
        // The detail can be an object with message, existing_project_id, and existing_project_title
        if (responseData) {
          const detail = responseData;
          
          if (typeof detail === 'object' && detail !== null) {
            existingProjectId = detail.existing_project_id;
            existingProjectTitle = detail.existing_project_title;
            logger.log('Parsed project ID:', existingProjectId);
            logger.log('Parsed project title:', existingProjectTitle);
          } else if (typeof detail === 'string') {
            // Try to parse if it's a JSON string
            try {
              const parsed = JSON.parse(detail);
              existingProjectId = parsed.existing_project_id;
              existingProjectTitle = parsed.existing_project_title;
            } catch (e) {
              // It's just a plain string message
              
              existingProjectTitle = 'an existing project';
            }
          }
        }
        
        // Fallback to safe default
        if (!existingProjectTitle) {
          existingProjectTitle = 'an existing project';
        }
        
        logger.log('Final title for display:', existingProjectTitle);
        
        const result = await showError(
          'Project Already Exists',
          `A project "${existingProjectTitle}" already exists for this recommendation. Would you like to view it?`,
          true, // showCancelButton
          'View Existing Project',
          'Cancel'
        );
        
        if (result.isConfirmed && existingProjectId) {
          window.location.href = `/projects/${existingProjectId}`;
        }
      } else {
        showError(
          'Error',
          error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to create project. Please try again.'
        );
      }
    } finally {
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Create Project from Recommendation
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Title */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Project Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Priority */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Priority
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Financial Details (Read-only display) */}
          <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Financial Details
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Cost:</span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                  ${formData.estimated_cost_aud?.toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Annual Savings:</span>
                <span className="ml-2 font-semibold text-emerald-600 dark:text-emerald-400">
                  ${formData.estimated_savings_aud?.toLocaleString()}/yr
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Payback:</span>
                <span className="ml-2 font-semibold text-blue-600 dark:text-blue-400">
                  {formData.payback_years} years
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">ROI:</span>
                <span className="ml-2 font-semibold text-purple-600 dark:text-purple-400">
                  {formData.roi_pct}%
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddToProjectModal;
